import { AlertCircle, CheckCircle, Loader2 } from "lucide-react";
import type { DepositStage } from "../../hooks/deposit/useDepositHoldFlow";

interface DepositHoldStatusProps {
  stage: DepositStage;
  amount: number;
  errorMessage: string | null;
  onRetry: () => void;
  retryCount: number;
  /** ISO8601 time of the planned automatic hold (`scheduled` stage). */
  scheduledAt?: string | null;
}

function formatScheduledDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatCurrency(amount: string | number): string {
  const num = typeof amount === "string" ? parseFloat(amount) : amount;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(num);
}

/**
 * Visual state for the deposit-hold flow. Mirrors the panel rendered by
 * quote-review's `<DepositHoldStatus>` (duplicated on purpose so changes
 * in either flow don't ripple into the other).
 */
export function DepositHoldStatus({
  stage,
  amount,
  errorMessage,
  onRetry,
  retryCount,
  scheduledAt,
}: DepositHoldStatusProps) {
  const formatted = formatCurrency(amount);

  if (stage === "scheduled") {
    const scheduledDate = formatScheduledDate(scheduledAt);
    return (
      <div
        role="status"
        className="rounded-lg border border-green-200 bg-green-50 p-4 flex items-start gap-3"
      >
        <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="font-medium text-green-900">Reservation confirmed</p>
          <p className="text-green-800 mt-1">
            {scheduledDate
              ? `Your security deposit will be authorized on ${scheduledDate}.`
              : "Your security deposit will be authorized before pickup; we will email you the details."}
          </p>
        </div>
      </div>
    );
  }

  if (stage === "succeeded") {
    return (
      <div className="rounded-lg border border-green-200 bg-green-50 p-4 flex items-start gap-3">
        <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="font-medium text-green-900">
            Security deposit authorized
          </p>
          <p className="text-green-800 mt-1">
            A {formatted} hold was placed on your card. Funds are released after
            the rental ends.
          </p>
        </div>
      </div>
    );
  }

  if (stage === "failed") {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 space-y-2">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-red-900">
              Reservation confirmed — deposit pending
            </p>
            <p className="text-red-800 mt-1">
              {errorMessage ||
                "We could not authorize the security deposit hold."}{" "}
              Your reservation is safe.
            </p>
          </div>
        </div>
        {retryCount < 2 && (
          <button
            type="button"
            onClick={onRetry}
            className="w-full inline-flex items-center justify-center rounded-md border border-red-300 bg-white px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50"
          >
            Retry deposit
          </button>
        )}
        {retryCount >= 2 && (
          <p className="text-xs text-red-700">
            We will reach out to resolve the deposit separately.
          </p>
        )}
      </div>
    );
  }

  if (stage === "skipped") {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="font-medium text-amber-900">
            Reservation confirmed — deposit will be processed separately
          </p>
          <p className="text-amber-800 mt-1">
            We will send you an email with a link to authorize the {formatted}{" "}
            security deposit hold.
          </p>
        </div>
      </div>
    );
  }

  if (stage === "needs_card") {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="font-medium text-amber-900">
            A credit card is required for the security deposit
          </p>
          <p className="text-amber-800 mt-1">
            {errorMessage ||
              `We could not reuse your rental card to authorize the ${formatted} security deposit. Please add a different credit card below.`}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 flex items-center gap-3">
      <Loader2 className="h-5 w-5 text-blue-600 animate-spin flex-shrink-0" />
      <div className="text-sm">
        <p className="font-medium text-blue-900">
          Authorizing security deposit…
        </p>
        <p className="text-blue-800 mt-0.5">
          {stage === "waiting_secret"
            ? "Waiting for confirmation from the payment processor."
            : `Placing a ${formatted} hold on your card.`}
        </p>
      </div>
    </div>
  );
}
